<template>
  <div>
    <DxDataGrid ref="dataGridRef" :data-source="dsParametri" key-expr="Cod" :allow-column-reordering="true"
      :allow-column-resizing="true" column-resizing-mode="nextColumn" :show-borders="true" :show-row-lines="true"
      :show-column-lines="false" :column-auto-width="true" class="grid-border" @row-removing="onRowRemoving">
      <DxSelection mode="single" />
      <DxFilterRow :visible="true" />
      <DxPaging :page-size="20" />
      <DxSorting mode="multiple" />
      <DxStateStoring :enabled="true" storage-key="GridParametriAplicatie" />
      <DxLoadPanel :enabled="true" />

      <DxEditing mode="cell" :allow-updating="false" :allow-adding="false" :allow-deleting="false" />

      <DxGridToolbar>
        <DxGridToolbarItem location="after">
          <template #default>
            <DxButton icon="plus" type="success" hint="Adaugă parametru" @click="onAddClick" />
          </template>
        </DxGridToolbarItem>
        <DxGridToolbarItem location="after">
          <template #default>
            <DxButton icon="refresh" hint="Reîncarcă" @click="onRefreshClick" />
          </template>
        </DxGridToolbarItem>
        <DxGridToolbarItem name="searchPanel" />
      </DxGridToolbar>

      <DxSearchPanel :visible="true" placeholder="Caută..." />

      <DxColumn data-field="Cod" caption="Cod" />
      <DxColumn data-field="Tip" caption="Tip" :calculate-display-value="getTipNume" />
      <DxColumn data-field="Valoare" caption="Valoare" :width="250" cell-template="valoareTemplate"
        :allow-filtering="false" :allow-sorting="false" />
      <template #valoareTemplate="{ data }">
        <span>{{ formatValoare(data.data) }}</span>
      </template>

      <DxColumn :width="110" cell-template="gridButtonsTemplate" caption="Acțiuni" alignment="center" />
      <template #gridButtonsTemplate="{ data }">
        <div class="grid-action-buttons">
          <i class="dx-icon-edit grid-btn-edit" title="Editează" @click="onEditClick(data.data)"></i>
          <i class="dx-icon-trash grid-btn-delete" title="Șterge" @click="confirmDelete(data.data)"></i>
        </div>
      </template>
    </DxDataGrid>

    <DxPopup v-model:visible="isModalVisible" :close-on-outside-click="false" :show-close-button="false"
      :show-title="true" :width="500" :height="300" :drag-enabled="true">
      <template #title>
        <div class="popup-header">
          <div class="popup-header-main">
            <span class="popup-header-icon" :class="isEditMode ? 'dx-icon-edit' : 'dx-icon-add'"></span>
            <div class="popup-header-texts">
              <div class="popup-header-title">{{ isEditMode ? 'Editare Parametru' : 'Adăugare Parametru' }}</div>
              <div class="popup-header-subtitle">
                <!-- {{ isEditMode ? 'Modifică valorile parametrului selectat' : 'Completează detaliile pentru noul
                parametru' }} -->
              </div>
            </div>
          </div>
          <div class="popup-header-meta">
            <span class="status-chip status-chip--draft">
              {{ isEditMode ? 'Editare' : 'Nou' }}
            </span>
          </div>
        </div>
      </template>

      <div class="popup-body-modern">
        <DxForm ref="formRef" :form-data="formData" label-mode="floating" :col-count="1"
          @field-data-changed="onFieldDataChanged">
          <DxSimpleItem data-field="Cod" :editor-options="{ disabled: isEditMode }">
            <DxRequiredRule message="Codul este obligatoriu" />
          </DxSimpleItem>

          <DxSimpleItem data-field="Tip" editor-type="dxSelectBox" :editor-options="tipSelectOptionsForm">
            <DxRequiredRule message="Tipul este obligatoriu" />
          </DxSimpleItem>

          <DxSimpleItem v-if="formData.Tip !== null && valoareType === 'bool'" data-field="Valoare"
            editor-type="dxCheckBox" :label="{ text: 'Activ' }" />

          <DxSimpleItem v-if="formData.Tip !== null && valoareType === 'date'" data-field="Valoare"
            editor-type="dxDateBox" :editor-options="dateOptions">
            <DxRequiredRule message="Valoarea este obligatorie" />
          </DxSimpleItem>

          <DxSimpleItem v-if="formData.Tip !== null && valoareType === 'number'" data-field="Valoare"
            editor-type="dxNumberBox" :editor-options="numberOptions">
            <DxRequiredRule message="Valoarea este obligatorie" />
          </DxSimpleItem>

          <DxSimpleItem v-if="formData.Tip !== null && valoareType === 'string'" data-field="Valoare"
            editor-type="dxTextBox" :editor-options="{ width: '100%' }">
            <DxRequiredRule message="Valoarea este obligatorie" />
          </DxSimpleItem>
        </DxForm>

        <div class="footer-fixed footer-modern">
          <div class="footer-right">
            <DxButton text="Salvează" type="success" width="120" height="32" styling-mode="outlined" @click="onSave" />
            <DxButton text="Închide" type="danger" width="120" height="32" styling-mode="outlined"
              @click="isModalVisible = false" />
          </div>
        </div>
      </div>
    </DxPopup>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ODataStore from 'devextreme/data/odata/store';
import AppSettings from '@/settings/appSettings';
import ERP_ParametriAplicatieService from '@/services/AUTH/ERP_ParametriAplicatie';
import http from '@/settings/apiSettings';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';

import { DxForm, DxSimpleItem, DxRequiredRule } from 'devextreme-vue/form';
import DxPopup from 'devextreme-vue/popup';
import { DxButton } from 'devextreme-vue/button';
import {
  DxDataGrid,
  DxColumn,
  DxFilterRow,
  DxLoadPanel,
  DxPaging,
  DxSearchPanel,
  DxSelection,
  DxSorting,
  DxStateStoring,
  DxToolbar as DxGridToolbar,
  DxItem as DxGridToolbarItem,
  DxEditing,
} from 'devextreme-vue/data-grid';

export default defineComponent({
  name: 'GridParametriAplicatie',
  components: {
    DxDataGrid, DxColumn, DxFilterRow, DxLoadPanel,
    DxPaging, DxSearchPanel, DxSelection, DxSorting,
    DxStateStoring, DxGridToolbar, DxGridToolbarItem, DxButton,
    DxEditing, DxPopup, DxForm, DxSimpleItem, DxRequiredRule,
  },
  data() {
    return {
      dsParametri: {} as ODataStore,
      tipValori: [] as any[],
      isModalVisible: false,
      isEditMode: false,
      isPopulating: false,
      formData: { Cod: '', Tip: null as number | null, Valoare: null as any },
      dateOptions: { type: 'datetime', displayFormat: 'dd.MM.yyyy HH:mm', useMaskBehavior: true, width: '100%', openOnFieldClick: true },
    };
  },
  computed: {
    tipSelectOptionsForm(): any {
      return {
        items: this.tipValori,
        valueExpr: 'ValoareFront',
        displayExpr: 'ValoareBack',
        width: '100%',
        placeholder: 'Selectează tipul...',
      };
    },
    valoareType(): string {
      if (this.formData.Tip === null) return 'none';
      const tip = this.tipValori.find((t: any) => Number(t.ValoareFront) === Number(this.formData.Tip));
      const back = (tip?.ValoareBack ?? '').toLowerCase();
      if (['bool', 'bit', 'boolean'].some(t => back.includes(t))) return 'bool';
      if (['date', 'datetime', 'month', 'year', 'timp', 'time', 'ora', 'luna', 'hour'].some(t => back.includes(t))) return 'date';
      if (['int', 'integer', 'decimal', 'float', 'numeric', 'money', 'real', 'smallint', 'tinyint', 'bigint'].some(t => back.includes(t))) return 'number';
      return 'string';
    },
    numberOptions(): any {
      if (this.formData.Tip === null) return { width: '100%' };
      const tip = this.tipValori.find((t: any) => Number(t.ValoareFront) === Number(this.formData.Tip));
      const back = (tip?.ValoareBack ?? '').toLowerCase();
      const isInt = ['int', 'integer', 'smallint', 'bigint'].some(t => back.includes(t));
      return { width: '100%', format: isInt ? '#0' : undefined, step: isInt ? 1 : undefined };
    }
  },
  methods: {
    onFieldDataChanged(e: any) {
      if (e.dataField === 'Tip' && !this.isPopulating) {
        this.formData.Valoare = null;
      }
    },
    getTipNume(rowData: any): string {
      const tip = this.tipValori.find((t: any) => Number(t.ValoareFront) === Number(rowData.Tip));
      return tip?.ValoareBack ?? String(rowData.Tip ?? '');
    },
    formatValoare(rowData: any): string {
      const val = rowData.Valoare;
      if (val === null || val === undefined || val === '') return '';
      const d = (val instanceof Date) ? val : (typeof val === 'string' && (val.includes('T') || val.includes('-'))) ? new Date(val) : null;
      const tip = this.tipValori.find((t: any) => Number(t.ValoareFront) === Number(rowData.Tip));
      const back = (tip?.ValoareBack ?? '').toLowerCase();
      const isDateType = ['date', 'datetime', 'timestamp', 'time', 'month', 'year', 'ora', 'luna', 'hour'].some(t => back.includes(t));
      if (isDateType || (d && !isNaN(d.getTime()))) {
        const dateObj = d || new Date(val);
        if (dateObj && !isNaN(dateObj.getTime())) {
          return `${String(dateObj.getDate()).padStart(2, '0')}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${dateObj.getFullYear()}`;
        }
      }
      if (['bool', 'bit', 'boolean'].some(t => back.includes(t))) {
        return (val === true || val === 1 || val === 'true' || val === '1') ? 'Da' : 'Nu';
      }
      return String(val);
    },
    createODataContext() {
      this.dsParametri = new ODataStore({
        url: AppSettings.API_Url_AuthOdata + 'ERP_ParametriAplicatie',
        key: 'Cod',
        keyType: 'String',
        version: 4,
        beforeSend: (e: any) => { e.headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt') }; },
      });
    },
    async loadTipValori() {
      try {
        const response = await http.clientAuthOdata.get('/ERP_TipValoare');
        const vals = response.data?.value ?? response.data ?? [];
        this.tipValori = vals.map((v: any) => ({ ...v, ValoareFront: Number(v.ValoareFront) }));
      } catch { this.tipValori = []; }
    },
    onAddClick() {
      this.isPopulating = true; this.isEditMode = false;
      this.formData.Cod = ''; this.formData.Tip = null; this.formData.Valoare = null;
      this.isModalVisible = true;
      this.$nextTick(() => { this.isPopulating = false; });
    },
    onEditClick(data: any) {
      if (!data) return;
      this.isPopulating = true; this.isEditMode = true;
      this.formData.Cod = data.Cod; this.formData.Tip = Number(data.Tip);
      this.formData.Valoare = this.parseValoare(data.Valoare, data.Tip);
      this.isModalVisible = true;
      setTimeout(() => { this.isPopulating = false; }, 200);
    },
    async confirmDelete(data: any) {
      if (!data) return;
      const result = await confirm("Sigur doriți să ștergeți acest parametru?", "Confirmare Ștergere");
      if (result) {
        try {
          await ERP_ParametriAplicatieService.delete(data.Cod);
          notify('Datele au fost șterse cu succes!', 'success', 1000);
          this.onRefreshClick();
        } catch { notify('Eroare la ștergere!', 'error', 2000); }
      }
    },
    async onSave() {
      const formInstance = (this.$refs.formRef as any)?.instance;
      if (!formInstance?.validate()?.isValid) return;
      try {
        if (this.isEditMode) {
          await ERP_ParametriAplicatieService.update(this.formData.Cod, this.formData);
          notify('Datele au fost actualizate cu succes!', 'success', 1000);
        } else {
          await ERP_ParametriAplicatieService.save(this.formData);
          notify('Parametrul a fost adăugat cu succes!', 'success', 1000);
        }
        this.isModalVisible = false;
        this.onRefreshClick();
      } catch (err: any) {
        const msg = err?.response?.data?.message ?? 'Eroare la salvare!';
        notify(msg, 'error', 2000);
      }
    },
    onRefreshClick() { this.createODataContext(); notify('Datele au fost actualizate cu succes!', 'success', 1000); },
    parseValoare(valoare: any, tipId: number | null): any {
      if (valoare === null || valoare === undefined) return null;
      const tip = this.tipValori.find((t: any) => Number(t.ValoareFront) === Number(tipId));
      if (!tip) return valoare;
      const back = (tip?.ValoareBack ?? '').toLowerCase();
      if (['date', 'datetime', 'month', 'year', 'ora', 'luna', 'timp', 'time', 'hour'].some(t => back.includes(t))) {
        const d = new Date(valoare); return isNaN(d.getTime()) ? valoare : d;
      }
      if (['bool', 'bit', 'boolean'].some(t => back.includes(t))) {
        return valoare === true || valoare === 1 || valoare === 'true' || valoare === '1';
      }
      if (['int', 'integer', 'decimal', 'float', 'numeric', 'money', 'real', 'smallint', 'tinyint', 'bigint'].some(t => back.includes(t))) {
        return !isNaN(Number(valoare)) ? Number(valoare) : valoare;
      }
      return valoare;
    },
    onRowRemoving(e: any) { e.cancel = true; }
  },
  async mounted() { await this.loadTipValori(); this.createODataContext(); },
});
</script>

<style scoped>
.grid-border {
  border-radius: 8px;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 18px;
  background: linear-gradient(90deg, #ff2c21, #ff6a4a);
  color: #fff;
  border-radius: 4px 4px 0 0;
}

.popup-header-main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.popup-header-icon {
  font-size: 20px;
}

.popup-header-texts {
  display: flex;
  flex-direction: column;
}

.popup-header-title {
  font-size: 15px;
  font-weight: 700;
}

.popup-header-subtitle {
  font-size: 11px;
  opacity: 0.85;
}

.status-chip {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background-color: rgba(255, 255, 255, 0.18);
}

.popup-body-modern {
  padding: 10px;
}

.footer-fixed {
  margin-top: 20px;
  padding: 12px 0;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #eee;
}

.footer-right {
  display: flex;
  gap: 8px;
}

.grid-action-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  font-size: 18px;
}

.grid-btn-edit {
  color: #1976d2;
  cursor: pointer;
}

.grid-btn-delete {
  color: #d32f2f;
  cursor: pointer;
}

:deep(.dx-button.dx-button-mode-outlined.dx-button-success) {
  border-color: #2e7d32;
  color: #2e7d32;
}

:deep(.dx-button.dx-button-mode-outlined.dx-button-danger) {
  border-color: #d32f2f;
  color: #d32f2f;
}
</style>
